<template>
  <div class="ui-box" :class="[bgType, `layout-${layout}`, { 'is-empty': isEmpty }]" :style="{ '--ui-box-tab-count': tabCount }">
    <div class="ui-box-nav">
      <div class="ui-box-nav-left">
        <slot name="nav-left"></slot>
      </div>
      <div class="ui-box-nav-right">
        <slot name="nav-right"></slot>
      </div>
    </div>
    <div class="ui-box-container">
      <slot name="container"></slot>
    </div>
    <slot></slot>
  </div>
</template>

<script setup>
// 两个正交维度共 4 种容器: bgType (色调) × layout (tab 数)
//   dark  / light    色调
//   single / multi   single 用异形切图(含 tab 区), multi 用平整背景
// isEmpty: 由使用方显式指定当前是否空态, 触发 $bg_container_empty 背景
defineProps({
  bgType: {
    type: String,
    default: 'dark'
  },
  layout: {
    type: String,
    default: 'multi',
    validator: (v) => ['single', 'multi'].includes(v)
  },
  isEmpty: {
    type: Boolean,
    default: false
  },
  // layout-multi 下 nav-left 的 tab 数, 决定 nav-left 宽度 = tabCount × 250 单位
  // (Safari 在 flex 容器里对含 vh 的 flex-basis 计算 max-content 有偏差, 显式给宽度)
  tabCount: {
    type: Number,
    default: 3
  }
})
</script>

<style lang="scss" scoped>
.ui-box {
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;
  height: 100%;

  .ui-box-nav {
    position: absolute;
    top: 0;
    width: 100%;
    height: 10%;     // 切图里 tab 行高度比例
    z-index: 2;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .ui-box-nav-left {
      position: relative;
      display: flex;
      justify-content: flex-start;
      align-items: center;
    }
    .ui-box-nav-right {
      display: flex;
      height: 100%;
      justify-content: center;
      align-items: center;
    }
  }

  .ui-box-container {
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    flex: 1;
    overflow: hidden;
    z-index: 1;
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;
  }

  // single: 异形切图整图作底, dark/light 共用同一套布局百分比
  // nav 用百分比对齐切图里的 tab 行, container 内缩贴合切图内容区
  &.layout-single {
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;

    .ui-box-nav-left {
      width: 19.6%;
      height: 100%;
      transform: translateY(27%);
    }
    .ui-box-nav-right {
      position: absolute;
      top: 0;
      right: 0;
      width: auto;
      height: 100%;
    }
    .ui-box-container {
      position: absolute;
      padding-top: 0;
    }

    :deep(.ui-tab) {
      padding: 6% 12% 0 0;
    }
  }

  &.layout-multi {
    // 顶部/斜边均匀 4 units 红条, 保留 1px overlap 避免缝隙
    // 权衡: boundary 列有 ~0.6px 加厚 (相对 4 单位红条 ~13%), 不仔细看不明显
    @include r(--ui-box-slant, 50);

    --ui-box-hilite: calc(4 / 55 * 100%);

    .ui-box-nav-left {
      height: 76.28%;
      // 显式 width = tab 单位宽 × tab 数; Safari 在 flex 容器里对含 vh 的 flex-basis 算 max-content 会偏小, 内容撑不满导致背景 (SVG + gradient) 只铺前部, 后面 tab 溢出到 nav-left 之外
      width: calc(#{get_vh(250)} * var(--ui-box-tab-count));
      @media screen and (max-aspect-ratio: 1472/828) {
        width: calc(#{get_vw(250)} * var(--ui-box-tab-count));
      }
      flex-shrink: 0;
      transform: translate(1%, 9%); // 视觉微调 nav-left 位置贴合 container
      align-self: flex-end;
      background:
        url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 50 55' preserveAspectRatio='none'><path d='M0 0L50 55H0V0Z' fill='%23F51922'/><path d='M0 4L46.36 55H0V4Z' fill='%236D0402'/></svg>") right top / var(--ui-box-slant) 100% no-repeat,
        linear-gradient(180deg, #F51922 0 var(--ui-box-hilite), #6D0402 var(--ui-box-hilite) 100%) left top / calc(100% - var(--ui-box-slant) + 1px) 100% no-repeat;
    }

    :deep(.ui-tab) {
      @include r(flex-basis, 250);
      flex-grow: 0;
      flex-shrink: 0;
      height: 100%;
      position: relative;
      @include r(padding-right, 30); // 50是完全无视两边斜边

      .label-box {
        .label-text { color: #C64F52; }
        .label-shadow { color: #540001; }
      }

      &.active {
        background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 250 55' preserveAspectRatio='none'><path d='M0 0H200L250 55H0V0Z' fill='%23F51922'/><path d='M0 4H200L246.36 55H0V4Z' fill='%23B91012'/></svg>") center/100% 100% no-repeat;

        .label-box {
          .label-text { color: #fff; }
          .label-shadow { color: rgba(#000, 0.4); }
        }
      }
    }

    .ui-box-container {
      padding-top: get_vh(72); // 内容避开顶部 tab 行
      padding-inline: 1.44% 1.32%;
      box-sizing: border-box;

      // [DEBUG] 半透明高亮 padding 内 content-box 区域, 确认完尺寸后删除
      // &::before {
      //  content: '';
      //  position: absolute;
      //  inset: get_vh(72) 1% 0 1%; // 与 padding-top / padding-inline 对齐
      //  background: rgba(0, 200, 255, 0.2);
      //  outline: 2px dashed rgba(0, 200, 255, 0.6);
      //  outline-offset: -2px;
      //  pointer-events: none;
      //  z-index: 1;
      // }
    }
  }

  // dark/light 决定大背景色调; single 挂 ui-box 整体, multi 挂 container 内层
  &.dark.layout-single  { background-image: $bg_container_dark; }
  &.dark.layout-multi   .ui-box-container { background-image: $bg_container_dark; }
  &.light.layout-single { background-image: $bg_container_light; }
  &.light.layout-multi  .ui-box-container { background-image: $bg_container_light; }

  // 空态背景 (由使用方通过 :is-empty prop 显式指定)
  &.is-empty.layout-single                  { background-image: $bg_container_empty; }
  &.is-empty.layout-multi .ui-box-container { background-image: $bg_container_empty; }
}
</style>
